/**
 * Logger Utility
 * Feature: 011-logger-migration
 *
 * 구조화된 로깅 유틸리티
 * - 4개 로그 레벨: debug, info, warn, error
 * - 환경별 기본 레벨: development=debug, production=info, test=warn
 * - 일관된 포맷: [timestamp] [LEVEL] [module] message
 * - 순환 참조 안전 처리
 */

// T010: LogLevel 타입 및 우선순위
export type LogLevel = "debug" | "info" | "warn" | "error";

const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

// T011: 환경별 기본 로그 레벨
function getDefaultLogLevel(): LogLevel {
  // 브라우저 환경에서는 process가 없음
  if (typeof process === "undefined" || !process.env) {
    return "info"; // 브라우저 기본값
  }

  // LOG_LEVEL 환경 변수가 있으면 우선 사용
  const envLevel = process.env.LOG_LEVEL?.toLowerCase() as LogLevel;
  if (envLevel && LOG_LEVEL_PRIORITY[envLevel] !== undefined) {
    return envLevel;
  }

  // NODE_ENV 기반 기본값
  const nodeEnv = process.env.NODE_ENV;
  switch (nodeEnv) {
    case "production":
      return "info";
    case "test":
      return "warn";
    case "development":
    default:
      return "debug";
  }
}

// T012: 로그 레벨 비교 함수
function shouldLog(currentLevel: LogLevel, minLevel: LogLevel): boolean {
  return LOG_LEVEL_PRIORITY[currentLevel] >= LOG_LEVEL_PRIORITY[minLevel];
}

// T14: 순환 참조 안전 직렬화
function safeStringify(obj: unknown): string {
  // Error 객체는 JSON.stringify로 직렬화되지 않으므로 특별 처리
  if (obj instanceof Error) {
    return JSON.stringify(
      {
        name: obj.name,
        message: obj.message,
        stack: obj.stack,
        // MongoDB/Mongoose 에러의 추가 정보
        ...("code" in obj ? { code: (obj as { code?: unknown }).code } : {}),
        ...("codeName" in obj ? { codeName: (obj as { codeName?: unknown }).codeName } : {}),
      },
      null,
      2
    );
  }

  const seen = new WeakSet();
  try {
    return JSON.stringify(
      obj,
      (_key, value) => {
        if (typeof value === "object" && value !== null) {
          if (seen.has(value)) {
            return "[Circular]";
          }
          seen.add(value);
        }
        return value;
      },
      2
    );
  } catch {
    return String(obj);
  }
}

// T13: 로그 메시지 포맷
function formatMessage(level: LogLevel, module: string | undefined, message: string): string {
  const timestamp = new Date().toISOString();
  const levelStr = level.toUpperCase().padEnd(5);
  const moduleStr = module ? `[${module}] ` : "";
  return `[${timestamp}] [${levelStr}] ${moduleStr}${message}`;
}

// 로거 옵션 인터페이스
export interface LoggerOptions {
  /** 모듈/컴포넌트 이름 (로그 출처 식별용) */
  module?: string;
  /** 로그 레벨 (환경 변수보다 우선) */
  level?: LogLevel;
}

// 로거 인터페이스
export interface Logger {
  debug(message: string, ...args: unknown[]): void;
  info(message: string, ...args: unknown[]): void;
  warn(message: string, ...args: unknown[]): void;
  error(message: string, ...args: unknown[]): void;
}

// T15: 로거 팩토리 함수
export function createLogger(options: LoggerOptions = {}): Logger {
  const { module, level } = options;
  // 레벨은 옵션 > 환경변수 > 기본값 순으로 결정
  const minLevel = level ?? getDefaultLogLevel();

  const log = (
    logLevel: LogLevel,
    consoleMethod: (...args: unknown[]) => void,
    message: string,
    args: unknown[]
  ) => {
    if (!shouldLog(logLevel, minLevel)) {
      return;
    }

    const formattedMessage = formatMessage(logLevel, module, message);

    if (args.length === 0) {
      consoleMethod(formattedMessage);
    } else {
      // 추가 인자가 있으면 안전하게 직렬화하여 출력
      const formattedArgs = args.map((arg) =>
        typeof arg === "object" ? safeStringify(arg) : String(arg)
      );
      consoleMethod(formattedMessage, ...formattedArgs);
    }
  };

  return {
    debug: (message: string, ...args: unknown[]) => log("debug", console.debug, message, args),
    info: (message: string, ...args: unknown[]) => log("info", console.log, message, args),
    warn: (message: string, ...args: unknown[]) => log("warn", console.warn, message, args),
    error: (message: string, ...args: unknown[]) => log("error", console.error, message, args),
  };
}
